/**
 * ShellContext — cross-component state for the app shell.
 *
 * Provides:
 *   - sidebar collapsed (desktop): persists to localStorage
 *   - mobile drawer open
 *   - which "expandable" sidebar rows are open (Origins/Docs)
 *   - the command-palette open/setOpen pair (Stage 6 replaces the
 *     state hook; the same context surface stays)
 *
 * Placed alongside the shell so Stage 5 pages can read it if they
 * need to (e.g. to toggle the sidebar from a deep-nested CTA).
 */

import { useMediaQuery } from '@/hooks/use-media-query';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const COLLAPSE_STORAGE_KEY = 'wv.dashboard.sidebar.collapsed';
const ORIGINS_EXPAND_STORAGE_KEY = 'wv.dashboard.sidebar.origins.expanded';
const DOCS_EXPAND_STORAGE_KEY = 'wv.dashboard.sidebar.docs.expanded';

type ShellContextValue = {
  /** Desktop collapsed state (≥1024px: honours user pref; 768–1023px: auto-collapsed default, can un-collapse). */
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
  /** Mobile-only drawer open state (<768px). */
  mobileOpen: boolean;
  setMobileOpen: (next: boolean) => void;
  /** Current viewport class. */
  isMobile: boolean;
  /** Expand/collapse per top-level sidebar entry (href → open?). */
  expandedHrefs: Record<string, boolean>;
  toggleExpanded: (href: string) => void;
  /** Right-hand Assistant pane open state. Stage 2 = blank body. */
  aiOpen: boolean;
  setAiOpen: (next: boolean) => void;
  /**
   * Org-switcher popover open state. Owned by the sidebar org pill
   * but surfaced here so the user pill menu's "Switch organisation"
   * row can open the same popover.
   */
  orgSwitcherOpen: boolean;
  setOrgSwitcherOpen: (next: boolean) => void;
};

const ShellContext = createContext<ShellContextValue | null>(null);

function readStoredBool(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    return raw === '1' || raw === 'true';
  } catch {
    return fallback;
  }
}

function writeStoredBool(key: string, value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // ignore (private mode, quota, etc.)
  }
}

export function ShellProvider({ children }: { children: ReactNode }) {
  // mobile (<768) — no sidebar; driven entirely by drawer open state.
  const isMobile = useMediaQuery('max-md');
  // 768-1023 — "tablet"; auto-collapse default per shell-spec §5.
  const isTablet = useMediaQuery('md:max-lg');

  // Default to collapsed at every breakpoint. Users who previously
  // expanded the sidebar keep their preference via the stored flag;
  // first-time visitors see the icon-only rail.
  const [collapsed, setCollapsedState] = useState<boolean>(() =>
    readStoredBool(COLLAPSE_STORAGE_KEY, true),
  );

  // Belt-and-braces: if the stored flag is missing (e.g. private
  // browsing cleared localStorage between sessions) force collapsed
  // on the tablet band too — the icon rail is still the correct
  // default there and avoids re-opening on resize.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hadStored = window.localStorage.getItem(COLLAPSE_STORAGE_KEY) != null;
    if (!hadStored && isTablet) {
      setCollapsedState(true);
    }
  }, [isTablet]);

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    writeStoredBool(COLLAPSE_STORAGE_KEY, next);
  }, []);

  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever we cross out of the mobile
  // breakpoint so state stays coherent on resize.
  useEffect(() => {
    if (!isMobile && mobileOpen) setMobileOpen(false);
  }, [isMobile, mobileOpen]);

  const [expandedHrefs, setExpandedHrefs] = useState<Record<string, boolean>>(() => ({
    '/origins': readStoredBool(ORIGINS_EXPAND_STORAGE_KEY, true),
    '/docs': readStoredBool(DOCS_EXPAND_STORAGE_KEY, true),
  }));

  const toggleExpanded = useCallback((href: string) => {
    setExpandedHrefs((prev) => {
      const nextValue = !prev[href];
      const next = { ...prev, [href]: nextValue };
      if (href === '/origins') writeStoredBool(ORIGINS_EXPAND_STORAGE_KEY, nextValue);
      if (href === '/docs') writeStoredBool(DOCS_EXPAND_STORAGE_KEY, nextValue);
      return next;
    });
  }, []);

  const [aiOpen, setAiOpen] = useState(false);
  // Close the AI pane when crossing to mobile (pane overlays the
  // island there; easier to just dismiss than stack panels).
  useEffect(() => {
    if (isMobile && aiOpen) setAiOpen(false);
  }, [isMobile, aiOpen]);

  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);

  const value = useMemo<ShellContextValue>(
    () => ({
      collapsed,
      setCollapsed,
      mobileOpen,
      setMobileOpen,
      isMobile,
      expandedHrefs,
      toggleExpanded,
      aiOpen,
      setAiOpen,
      orgSwitcherOpen,
      setOrgSwitcherOpen,
    }),
    [
      collapsed,
      setCollapsed,
      mobileOpen,
      isMobile,
      expandedHrefs,
      toggleExpanded,
      aiOpen,
      orgSwitcherOpen,
    ],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellContextValue {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useShell must be used inside <ShellProvider>');
  return ctx;
}
